import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../common/apiClient'
import { useUserPermissions } from '../../contexts/UserPermissionsContext'
import type { AccessFlags } from '../../contexts/UserPermissionsContext'
import { getArticles } from '../../services/handbookService'
import { articleResources, buildResources, type Policy, type Resource } from './model'
import { HiShieldCheck } from 'react-icons/hi2'
import { useCachedAppCatalogue } from './useCachedAppCatalogue'
import type { AppCatalogueScope } from './appCatalogueCache'
import { useShutdownRosterAccess } from '../leave/hooks/useShutdownRosterAccess'
import { policyReaderPath } from '../../common/policyReader'

export function useHomeResources(email: string) {
  const { userPermissions, accessFlags, accessFlagsEmail, isLoadingAccessFlags, isLoadingOverride, loggedInUserEmail } = useUserPermissions()
  const shutdownAccess = useShutdownRosterAccess(loggedInUserEmail)
  const normalizedEmail = email.trim().toLowerCase()
  const identityReady = Boolean(normalizedEmail) && !isLoadingOverride
  const audienceReady = identityReady && userPermissions.Email?.trim().toLowerCase() === normalizedEmail
  const ready = audienceReady && !isLoadingAccessFlags && accessFlagsEmail?.trim().toLowerCase() === normalizedEmail
  const handbookAccess = ready && accessFlags.systemDev === true
  const scope: AppCatalogueScope | null = audienceReady ? {
    email: normalizedEmail, team: String(userPermissions.Team__c || ''),
    company: String(userPermissions.Company__c || ''), location: String(userPermissions.Division_Location__c || ''),
  } : null
  // Fetch navigation metadata alongside the access check, not after it.
  const teamApps = useCachedAppCatalogue(scope, 'team-apps')
  const services = useCachedAppCatalogue(scope, 'services')
  const safeFlags = useMemo(() => {
    if (ready) return accessFlags
    const denied = { ...accessFlags }
    for (const key of Object.keys(denied) as (keyof AccessFlags)[]) {
      if (key === 'notesLocationFilter') denied[key] = null
      else denied[key] = false
    }
    return denied
  }, [ready, accessFlags])
  const documents = useQuery({
    queryKey: ['home-preview', 'documents-visible', email], enabled: ready,
    queryFn: () => apiClient.get<{ visible: boolean }>(`/api/documents/sidebar/visibility?email=${encodeURIComponent(email)}`), staleTime: 300_000,
  })
  const policiesVisible = useQuery({
    queryKey: ['home-preview', 'policies-visible', email], enabled: ready,
    queryFn: () => apiClient.get<{ visible: boolean }>(`/api/policies/sidebar/visibility?email=${encodeURIComponent(email)}`), staleTime: 300_000,
  })
  const policies = useQuery({
    queryKey: ['home-preview', 'policies', email], enabled: ready && policiesVisible.data?.visible === true,
    queryFn: () => apiClient.get<Policy[]>(`/api/policies/mine?email=${encodeURIComponent(email)}`), staleTime: 60_000,
  })
  const handbook = useQuery({
    queryKey: ['home-preview', 'handbook', email], enabled: handbookAccess,
    queryFn: async () => {
      // The existing endpoint returns articles with their content. Keep the preview
      // index in memory and only expose published articles, including in View As.
      const response = await getArticles()
      if (!response.success) throw new Error('Handbook unavailable')
      return response.data
    }, staleTime: 300_000,
  })
  // Universal shortcuts can render immediately. Everything permission-dependent
  // (including all cached app links) still waits for the current access check.
  const resources = useMemo(() => identityReady ? buildResources(ready ? userPermissions : {}, ready ? normalizedEmail : '', safeFlags,
    ready && documents.data?.visible === true, ready && policiesVisible.data?.visible === true,
    ready ? teamApps.data || [] : [], ready ? services.data || [] : [], { shutdownRosterAccess: ready && shutdownAccess.canAccess }) : [],
  [identityReady, ready, userPermissions, normalizedEmail, safeFlags, documents.data, policiesVisible.data, teamApps.data, services.data, shutdownAccess.canAccess])
  const knowledge = useMemo<Resource[]>(() => [
    ...(handbookAccess ? articleResources(handbook.data || []) : []),
    ...(ready && policiesVisible.data?.visible === true ? policies.data || [] : []).map(policy => ({ id: `policy-${policy.policy_id}`, label: policy.name,
      path: policyReaderPath(policy.policy_id), detail: `${policy.office} · Company policy`,
      keywords: 'policy guidance', kind: 'Knowledge' as const, icon: HiShieldCheck })),
  ], [handbookAccess, handbook.data, ready, policiesVisible.data, policies.data])
  const loading = !ready || services.isLoading || (Boolean(scope?.team) && teamApps.isLoading)
  const visibilityLoading = !ready || documents.isLoading || policiesVisible.isLoading
  const errors = [services.isError && 'company apps', teamApps.isError && 'team apps',
    handbookAccess && handbook.isError && 'handbook', documents.isError && 'document access',
    (policies.isError || policiesVisible.isError) && 'policies'].filter(Boolean) as string[]
  return { resources, knowledge, ready, loading, visibilityLoading, shortcutsLoading: loading || visibilityLoading, errors, policies: ready ? policies.data || [] : [], handbookAccess,
    documentsVisible: ready && documents.data?.visible === true,
    knowledgeLoading: (handbookAccess && handbook.isLoading) || policies.isLoading, accessFlags: safeFlags,
    retry: () => { [teamApps, services, documents, policiesVisible, policies, ...(handbookAccess ? [handbook] : [])].forEach(query => {
      if (query.isError) void query.refetch()
    }) } }
}
